import type { HtmlToTextOptions } from "html-to-text";
import type { FieldPath, FieldValues, UseFormReturn } from "react-hook-form";

import { convert } from "html-to-text";
import { useEffect, useRef } from "react";

import { toast } from "@/components/toast/toast-queue";
import { useIsRequestFormDirty } from "@/hooks/useIsRequestFormDirty";

import { MAX_TEXT_LENGTH } from "./characterLimit";

export interface CharacterLimitField<TFieldValues extends FieldValues> {
  convertOptions?: HtmlToTextOptions;
  label: string;
  maxLength?: number;
  path: FieldPath<TFieldValues>;
}

export function useCharacterLimitToast<TFieldValues extends FieldValues>({
  fields,
  formMethods,
}: {
  fields: readonly CharacterLimitField<TFieldValues>[];
  formMethods: UseFormReturn<TFieldValues>;
}) {
  const { trigger, watch } = formMethods;
  const paths = fields.map((field) => field.path);
  const watchedValues = watch(paths);

  useEffect(() => {
    trigger(paths);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...watchedValues, trigger]);

  // Autosave resets the form and wipes errors, so restore the length errors
  useEffect(
    () =>
      useIsRequestFormDirty.subscribe(
        (state) => state.isDirty,
        (isDirty) => {
          if (!isDirty) trigger(fields.map((field) => field.path));
        },
      ),
    [fields, trigger],
  );

  const overLimitByPath = fields.map((field, index) => {
    const value = (watchedValues[index] as string | undefined) ?? "";
    const length = convert(value, field.convertOptions).length;
    const maxLength = field.maxLength ?? MAX_TEXT_LENGTH;

    return length > maxLength;
  });
  const overLimitSignature = overLimitByPath.map(Number).join("");

  const activeToastKeysRef = useRef<Map<string, string>>(new Map());

  const closeAllActiveToasts = () => {
    for (const key of activeToastKeysRef.current.values()) {
      toast.close(key);
    }
    activeToastKeysRef.current.clear();
  };

  useEffect(() => {
    for (const [index, field] of fields.entries()) {
      const isOverLimit = overLimitByPath[index];
      const activeKey = activeToastKeysRef.current.get(field.path);

      if (isOverLimit && !activeKey) {
        const key = toast.add({
          description: `${field.label} is over the character limit`,
          title: "Character limit exceeded",
          type: "error",
        });

        activeToastKeysRef.current.set(field.path, key);
      } else if (!isOverLimit && activeKey) {
        toast.close(activeKey);
        activeToastKeysRef.current.delete(field.path);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overLimitSignature]);

  useEffect(() => closeAllActiveToasts, []);
}
